import { FullAsinWithAudits } from "../../server/asin-central/model";
import React, { useEffect, useState } from "react";
import CopyButton from "../oneClickLaunch/CopyButton";
import { Link, Popover, StatusIndicator } from "@amzn/awsui-components-react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { decryptCustomerId } from "@amzn/amazon-id";
import { Feature, trackButtonClick } from "../telemetry/telemetry";
import { getMakoWeblabString } from "../app/api";
import { useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import { AuthorizedAttribute } from "../../from-server/brass/model";
import logger from "../../art-logger-config";
import { getPrimaryDomainName } from "../../from-server/acs/acs-service";
import { isProdStage } from "../../util/stageUtils";

const defaultWeblabString = "NONE";

// Since we're in a popover, the CopyToClipboard function used elsewhere will not work.
// This function is a bit of a hack to add an element to the DOM that we can copy from and then removes it.
const copyToClipboard = (input: string) => {
  const tempInput = document.createElement("input");
  tempInput.setAttribute("aria-hidden", "true");

  tempInput.value = input;
  document.body.appendChild(tempInput);
  tempInput.select();

  try {
    document.execCommand("copy");
  } catch (err) {
    console.error("Error copying to clipboard: ", err);
  }

  document.body.removeChild(tempInput);
};

function getSelectionCentralLink(asin: string, marketplaceId: string) {
  return `https://selection${
    isProdStage() ? "" : "-beta.integ"
  }.amazon.com/item/${marketplaceId}/${asin}/)`;
}

function getCsiLink(asin: string, marketplaceId: string) {
  return `https://csi.${
    isProdStage() ? "" : "vipinteg."
  }amazon.com/view?view=blame_o&item_id=${asin}&marketplace_id=${marketplaceId}`;
}

function getDetailPageLink(asin: string, obfuscatedMarketplaceId: string) {
  const marketplaceUrl =
    getPrimaryDomainName(obfuscatedMarketplaceId) || "www.amazon.com";
  return `https://${marketplaceUrl}/dp/${asin}?ignorePSLD=1`;
}

function getTownsendLink(partNumber: string) {
  return `https://devices${
    isProdStage() ? "" : "-beta"
  }.plm.aws.com/townsend/part/detail/${partNumber}`;
}

export function getJourneysLink(countryCode: string, asin: string) {
  return `https://${
    isProdStage() ? "" : "beta."
  }detail-page.journeys.a2z.com/asin/${countryCode}/${asin}?tab=Non-Confidential+Attributes`;
}

function userIsAuthorizedForWeblab(editableAttributes: AuthorizedAttribute[]) {
  const result = editableAttributes.some(
    (attribute) =>
      attribute.attributeName === "view_weblab_displaypage" &&
      attribute.authorized
  );
  return result;
}

function displayAsinLinks(
  asin: string,
  item: FullAsinWithAudits,
  componentName: Feature
) {
  const [weblabString, setWeblabString] = useState(defaultWeblabString);
  const [hasAsinLinkBeenClicked, setHasAsinLinkBeenClicked] = useState(false);
  const { editableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );

  useEffect(() => {
    async function getWeblabData() {
      if (!userIsAuthorizedForWeblab(editableAttributes)) {
        // User not able to see weblab string.
        return;
      }

      try {
        const weblabString = await getMakoWeblabString(
          asin,
          item.obfuscatedMarketplaceId
        );
        logger.info("G2S2 response: " + weblabString);
        setWeblabString(weblabString);
      } catch (error: any) {
        logger.error("Error getting weblab string: " + error);
      }
    }

    // We only want to fetch the string AFTER the user clicks on the asin.
    if (hasAsinLinkBeenClicked) {
      getWeblabData();
    }
  }, [editableAttributes, hasAsinLinkBeenClicked]);

  return (
    <span style={{ display: "flex", alignItems: "center" }}>
      <CopyButton asin={asin} componentName={componentName}></CopyButton>
      <Popover
        position="top"
        size="medium"
        triggerType="text"
        content={
          <SpaceBetween size="xs" direction={"vertical"}>
            <Link
              external
              href={getDetailPageLink(asin, item.obfuscatedMarketplaceId)}
              onFollow={() =>
                trackButtonClick(componentName, "DetailPageLink", {
                  asin: asin,
                  marketplace: item.obfuscatedMarketplaceId,
                })
              }
            >
              Detail Page
            </Link>
            {
              // Link should only be displayed after we've gotten a response from G2S2 and the response contains real data.
              userIsAuthorizedForWeblab(editableAttributes) &&
                weblabString !== "NOT_FOUND" &&
                weblabString !== defaultWeblabString && (
                  <Popover
                    size="small"
                    position="top"
                    triggerType="custom"
                    dismissButton={false}
                    content={
                      <StatusIndicator type="success">
                        Weblab String Copied
                      </StatusIndicator>
                    }
                  >
                    <Link
                      external
                      href={getDetailPageLink(
                        asin,
                        item.obfuscatedMarketplaceId
                      )}
                      onFollow={() => {
                        copyToClipboard(weblabString);
                        trackButtonClick(
                          componentName,
                          "WeblabDetailPageLink",
                          {
                            asin: asin,
                            obfuscatedMarketplaceId:
                              item.obfuscatedMarketplaceId,
                          }
                        );
                      }}
                    >
                      Weblab Detail Page
                    </Link>
                  </Popover>
                )
            }
            <Link
              external
              href={getSelectionCentralLink(
                asin,
                decryptCustomerId(item.obfuscatedMarketplaceId)
              )}
              onFollow={() =>
                trackButtonClick(componentName, "SelectionCentralLink", {
                  asin: asin,
                  marketplace: item.obfuscatedMarketplaceId,
                })
              }
            >
              Selection Central
            </Link>
            <Link
              external
              href={getJourneysLink(item.marketplace, asin)}
              onFollow={() =>
                trackButtonClick(componentName, "JourneysLink", {
                  asin: asin,
                  marketplace: item.obfuscatedMarketplaceId,
                })
              }
            >
              Journeys
            </Link>
            <Link
              external
              href={getCsiLink(
                asin,
                decryptCustomerId(item.obfuscatedMarketplaceId)
              )}
              onFollow={() =>
                trackButtonClick(componentName, "CsiLink", {
                  asin: asin,
                  marketplace: item.obfuscatedMarketplaceId,
                })
              }
            >
              CSI
            </Link>
            {item.partNumber !== undefined &&
              item.asinType !== "extended_warranty" && (
                <Link
                  external
                  href={getTownsendLink(item.partNumber)}
                  onFollow={() =>
                    trackButtonClick(componentName, "TownsendLink", {
                      asin: asin,
                      marketplace: item.obfuscatedMarketplaceId,
                    })
                  }
                >
                  Townsend
                </Link>
              )}
          </SpaceBetween>
        }
      >
        <Link onFollow={() => setHasAsinLinkBeenClicked(true)}>{asin}</Link>
      </Popover>
    </span>
  );
}

export default (props: {
  item: FullAsinWithAudits;
  componentName: Feature;
}) => {
  const attributeName = "asin";
  const asin = props.item[attributeName as keyof FullAsinWithAudits] as string;
  if (!asin) {
    return <>-</>;
  }
  return displayAsinLinks(asin, props.item, props.componentName);
};
